import React, { useEffect, useState } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { QrCode } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * PayOnlineDialog — customer picks a UPI mode and either taps a UPI app to
 * auto-open with the amount pre-filled OR scans a QR from a laptop.
 *
 * Two modes:
 *   variant="order"    → per-order (orderNumber prop)
 *   variant="combined" → all-pending-orders combined (mobile prop)
 *
 * No gateway integration needed — UPI intent URLs work on all Indian UPI
 * apps natively. Workshop team manually verifies the payment on their end
 * (bank SMS / app notification) and marks it in the admin panel.
 */
export default function PayOnlineDialog({
  open,
  onClose,
  orderNumber,       // used when variant === "order"
  balance,
  variant = "order",
  mobile,            // used when variant === "combined"
  combinedNote,      // display note in dialog for combined variant
}) {
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [selected, setSelected] = useState(null);

  const isCombined = variant === "combined";
  const optionsUrl = isCombined
    ? `${API}/public/pay-combined/${mobile}/payment-options`
    : `${API}/public/track/${orderNumber}/payment-options`;
  const qrUrlFor = (modeKey) => isCombined
    ? `${API}/public/pay-combined/${mobile}/upi-qr?mode_key=${encodeURIComponent(modeKey)}&t=${Date.now()}`
    : `${API}/public/track/${orderNumber}/upi-qr?mode_key=${encodeURIComponent(modeKey)}&t=${Date.now()}`;
  const txnNote = isCombined
    ? (combinedNote || "Combined balance")
    : `Order ${orderNumber}`;
  const identityLabel = isCombined
    ? `Combined ${info?.order_count || ""} order${info?.order_count === 1 ? "" : "s"}`
    : `Order ${orderNumber}`;

  useEffect(() => {
    if (!open) return;
    if (!isCombined && !orderNumber) return;
    if (isCombined && !mobile) return;
    setLoading(true);
    setErr("");
    setSelected(null);
    (async () => {
      try {
        const { data } = await axios.get(optionsUrl);
        setInfo(data);
        if (data?.upi_options?.length) setSelected(data.upi_options[0]);
      } catch (e) {
        setErr(e.response?.status === 404 ? "Order not found" : "Payment options load nahi ho paye");
      } finally { setLoading(false); }
    })();
  }, [open, orderNumber, mobile, variant]);

  const buildUpiParams = () => {
    if (!selected) return "";
    return new URLSearchParams({
      pa: selected.upi_id,
      pn: selected.payee_name || info?.business_name || "Shobhit Creation",
      am: String(Number(balance || 0).toFixed(2)),
      cu: "INR",
      tn: txnNote,
    }).toString();
  };

  const openUpiApp = (app) => {
    if (!selected) return;
    const q = buildUpiParams();
    // Force the *specific* UPI app so WhatsApp Pay / BHIM don't hijack the
    // intent. Android uses `intent://` with an explicit `package=`; iOS
    // uses each app's proprietary URL scheme.
    const ua = navigator.userAgent || "";
    const isAndroid = /android/i.test(ua);
    const isIOS = /iPhone|iPad|iPod/i.test(ua);

    const androidPackages = {
      gpay: "com.google.android.apps.nbu.paisa.user",
      phonepe: "com.phonepe.app",
      paytm: "net.one97.paytm",
    };
    const iosSchemes = {
      gpay: `gpay://upi/pay?${q}`,
      phonepe: `phonepe://pay?${q}`,
      paytm: `paytmmp://pay?${q}`,
    };

    let url;
    if (app && isAndroid && androidPackages[app]) {
      url = `intent://pay?${q}#Intent;scheme=upi;package=${androidPackages[app]};end`;
    } else if (app && isIOS && iosSchemes[app]) {
      url = iosSchemes[app];
    } else {
      url = `upi://pay?${q}`;
    }
    window.location.href = url;
  };

  const qrSrc = selected ? qrUrlFor(selected.key) : "";

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="max-w-md bg-slate-900 border-slate-700 text-white rounded-sm p-0 overflow-hidden"
        data-testid="pay-online-dialog"
      >
        <DialogHeader className="px-5 pt-5 pb-3 border-b border-slate-800">
          <div className="text-[10px] font-mono-num tracking-widest text-emerald-400">// SECURE PAYMENT · UPI</div>
          <DialogTitle className="font-heading text-2xl mt-1">
            Pay ₹{Number(balance || 0).toLocaleString("en-IN")}
          </DialogTitle>
          <p className="text-xs text-slate-400 mt-1">
            <span className="font-mono-num text-slate-300">{identityLabel}</span> · Direct UPI transfer
          </p>
        </DialogHeader>

        <div className="p-5 space-y-4">
          {loading && (
            <div className="text-center text-sm text-slate-400 py-6" data-testid="pay-loading">
              Options load ho rahe hain…
            </div>
          )}
          {err && (
            <div className="p-3 rounded-sm bg-red-500/10 border border-red-500/40 text-red-300 text-sm" data-testid="pay-error">
              {err}
            </div>
          )}
          {!loading && info && !info.payments_supported && (
            <div className="p-4 rounded-sm bg-amber-500/10 border border-amber-500/40 text-amber-200 text-sm" data-testid="pay-not-supported">
              Abhi online payment enabled nahi hai. Kripya <b>workshop</b> ko call karke Cash / bank transfer kar dijiye.
            </div>
          )}
          {!loading && info?.payments_supported && selected && (
            <div className="border-t border-slate-800 pt-4">
              <div className="text-[10px] font-mono-num tracking-widest text-slate-500 mb-3 flex items-center gap-1.5">
                <QrCode size={12} /> SCAN QR TO PAY
              </div>
              <div className="flex items-start gap-3">
                <div className="bg-white p-2 rounded-sm shrink-0" data-testid="pay-qr">
                  <img
                    src={qrSrc}
                    alt="Scan to pay"
                    className="w-44 h-44 object-contain"
                    onError={(e) => { e.currentTarget.style.opacity = "0.3"; }}
                  />
                </div>
                <div className="text-xs text-slate-300 leading-relaxed">
                  <div><span className="text-slate-500 font-mono-num">TO</span> {selected.payee_name}</div>
                  <div><span className="text-slate-500 font-mono-num">UPI</span> <span className="font-mono-num text-emerald-300">{selected.upi_id}</span></div>
                  <div><span className="text-slate-500 font-mono-num">AMT</span> ₹{Number(balance || 0).toLocaleString("en-IN")}</div>
                  <div className="mt-3 text-[10px] text-slate-500">
                    Kisi bhi UPI app (GPay / PhonePe / Paytm etc.) mein "Scan QR" khol ke ye code scan kijiye. Payment ke baad workshop ko WhatsApp screenshot bhej dijiye.
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="px-5 py-3 border-t border-slate-800 flex justify-end">
          <Button
            variant="outline"
            onClick={onClose}
            className="rounded-sm h-10 border-slate-600 text-slate-300 hover:bg-slate-800"
            data-testid="pay-close-btn"
          >
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function UpiAppButton({ label, tone, onClick, testid }) {
  return (
    <button
      onClick={onClick}
      className={
        "h-16 rounded-sm bg-gradient-to-br " + tone +
        " text-white font-heading text-xs font-black tracking-widest shadow-md active:scale-95 transition-transform"
      }
      data-testid={testid}
    >
      {label}
    </button>
  );
}
